/**
 * Every planet is a place, and places are recognisable before you read their
 * name. A planet's physical signature — its tilt, its rings, its moons — is
 * derived from its colour seed, so it is fixed for the life of the planet and
 * identical on every device.
 *
 * The one rule this file must not break: traits carry identity, never meaning.
 * Size is importance and colour is health; those two channels say how the world
 * is doing. A ring says only "this is Business", the way a coastline does.
 */

/** mulberry32: small, fast, and stable across engines — which determinism needs. */
function rng(seed: number): () => number {
  let a = (seed >>> 0) || 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface RingTrait {
  /** Multiples of the planet's radius. */
  innerScale: number;
  outerScale: number;
  opacity: number;
}

export interface MoonTrait {
  /** Multiples of the planet's radius. */
  distance: number;
  size: number;
  /** Radians per second. */
  speed: number;
  phase: number;
}

export interface PlanetPalette {
  /** What the world is made of, at its middle and at its poles. */
  equator: string;
  pole: string;
  /** Faint banding, for the gas bodies. Null on rock. */
  band: string | null;
}

/**
 * Colours you would expect a planet to be: rust, pale gold, ice, slate, deep
 * blue. Deliberately no strong green and no strong red — those two belong to
 * the health ramp, and a world that happened to be born green would read as a
 * healthy one forever.
 */
const PALETTE: PlanetPalette[] = [
  { equator: '#b4653a', pole: '#d9a27a', band: null }, // rust
  { equator: '#c9a068', pole: '#eed7ab', band: '#b08b57' }, // banded tan
  { equator: '#3e6fa8', pole: '#8fb6dd', band: '#33608f' }, // deep blue
  { equator: '#8fb9c9', pole: '#e2f0f5', band: null }, // ice
  { equator: '#82878f', pole: '#b9bec7', band: null }, // slate
  { equator: '#4f8f8a', pole: '#9ed0c9', band: null }, // teal
  { equator: '#a8746f', pole: '#d8a9a3', band: null }, // dusty rose
  { equator: '#7b6fa0', pole: '#b3a6d2', band: '#6a5f8c' }, // violet haze
  { equator: '#d8c48f', pole: '#f4e7c4', band: '#c0a874' }, // pale gold
];

export interface PlanetTraits {
  /** Axial tilt in radians. Rings and moons share it, as a real system does. */
  tilt: number;
  /** Radians per second about the planet's own axis. */
  spin: number;
  /** Equatorial bulge, as a y-scale. A fast, light world is visibly squashed. */
  flattening: number;
  /** Faceted rock or a smooth gas body. Sets the geometry's subdivision. */
  surface: 'rocky' | 'smooth';
  ring: RingTrait | null;
  moons: MoonTrait[];
  palette: PlanetPalette;
}

/**
 * Derived, never stored: the seed is already on the planet, so this needs no
 * migration and no extra round trip, and an existing planet keeps the face it
 * has always had.
 */
export function planetTraits(colourSeed: number): PlanetTraits {
  const next = rng(colourSeed);

  const tilt = 0.12 + next() * 0.44;
  const spin = 0.08 + next() * 0.22;
  const surface: PlanetTraits['surface'] = next() < 0.55 ? 'rocky' : 'smooth';
  const palette = PALETTE[Math.floor(next() * PALETTE.length)] as PlanetPalette;
  const flattening = 1 - next() * 0.1;

  // Getting on for half are ringed. A universe is usually four or five planets,
  // so a one-in-three chance often produced none at all — and a landmark nobody
  // has is not a landmark.
  const hasRing = next() < 0.45;
  // Narrow and faint on purpose: a ring is a name tag, not a feature. Anything
  // wider starts competing with size, which already means importance.
  const innerScale = 1.35 + next() * 0.2;
  const ring: RingTrait | null = hasRing
    ? {
        innerScale,
        outerScale: innerScale + 0.28 + next() * 0.4,
        opacity: 0.16 + next() * 0.16,
      }
    : null;

  const moonCount = next() < 0.3 ? 0 : next() < 0.75 ? 1 : 2;
  const moons: MoonTrait[] = [];
  for (let i = 0; i < moonCount; i += 1) {
    moons.push({
      distance: 2.4 + i * 1.1 + next() * 0.6,
      size: 0.16 + next() * 0.12,
      speed: 0.5 + next() * 0.5,
      phase: next() * Math.PI * 2,
    });
  }

  return { tilt, spin, flattening, surface, ring, moons, palette };
}
